import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Container,
  Alert
} from '@mui/material';
import axios from 'axios';

interface LoginProps {
  onLogin: () => void;
}

interface TokenResponse {
  access_token: string;
  token_type: string;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:18000';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Send password to backend for verification
      const response = await axios.post<TokenResponse>(`${API_URL}/api/auth/login`, {
        password: password
      });
      
      // Store the JWT token in localStorage
      localStorage.setItem('seoTrackerToken', response.data.access_token);
      localStorage.setItem('seoTrackerAuthenticated', 'true');
      
      // Call the onLogin callback to update parent component state
      onLogin();
    } catch (err: any) {
      if (err.response && err.response.status === 401) {
        setError('Incorrect password. Please try again.');
      } else {
        setError('An error occurred. Please try again.');
        console.error('Login error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            SEO Tracker Login
          </Typography>
          
          <Typography variant="body1" align="center" sx={{ mb: 3 }}>
            Enter the shared password to access the SEO Tracker tool
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Log In'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
